import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Import, Play, ChevronDown, Download, Info } from 'lucide-react';
import clsx from 'clsx';
import { importService } from '@services';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { useImportJobsPageData } from '@hooks/useImportJobsPageData';
import { useDismissable } from '@hooks/useDismissable';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import ListPageHeader from '@components/common/ListPageHeader';
import DashboardFilterChip from '@components/common/DashboardFilterChip';
import InfoBanner from '@components/common/InfoBanner';
import { FilterButtons } from '@components/common/FilterButtons';
import { RZTable } from '@components/common/table';
import { showToast } from '@utils/toast';
import type { TableColumn } from '@app-types';
import type { ImportHistoryRecord, ImportStatus, ImportType } from '@app-types/import';
import { formatMediumDateTime } from '@utils/dateUtils';
import styles from './ImportJobsTab.module.css';

// The Admin launch page's Jobs Pending Review tile opens this tab with ?completion=pending.
const COMPLETION_PARAM = 'completion';
const COMPLETION_PENDING = 'pending';

const jobTypeFilters = [
  { key: 'all', label: 'All' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'manual', label: 'Manual Import' },
  { key: 'on_demand', label: 'On Demand' },
];

const importJobColumns: TableColumn[] = [
  {
    key: 'started_at',
    label: 'Date Executed',
    width: '200px',
    defaultVisible: true,
    alwaysVisible: true,
    sortable: true,
    filterable: false,
  },
  {
    key: 'job_type',
    label: 'Job Type',
    width: '140px',
    defaultVisible: true,
    filterKey: 'job_type',
    sortable: true,
  },
  {
    key: 'import_type',
    label: 'Import Type',
    width: '150px',
    defaultVisible: true,
    filterKey: 'import_type',
    sortable: true,
  },
  {
    key: 'file_name',
    label: 'File',
    defaultVisible: true,
    filterKey: 'file_name',
    sortable: true,
  },
  {
    key: 'result',
    label: 'Result',
    width: '180px',
    defaultVisible: true,
    filterKey: 'import_status',
    sortable: true,
  },
];

const templateOptions: { importType: ImportType; label: string }[] = [
  { importType: 'contacts', label: 'Contacts' },
  { importType: 'contracts', label: 'Contracts' },
];

const columnSortMapping: Record<string, string> = {
  started_at: 'started_at',
  job_type: 'job_type',
  import_type: 'import_type',
  file_name: 'file_name',
  result: 'import_status',
};

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getJobTypeBadgeLabel(record: ImportHistoryRecord): string {
  if (record.job_type === 'scheduled') {
    return 'Scheduled';
  }
  if (record.job_type === 'on_demand') {
    return `On Demand (${record.imported_by_name || 'Unknown'})`;
  }
  return `Manual Import (${record.imported_by_name || 'Unknown'})`;
}

function getDisplayStatus(record: ImportHistoryRecord): ImportStatus {
  return record.is_completed ? 'Completed' : record.import_status;
}

function getResultBadgeVariant(status: ImportStatus): 'success' | 'warning' | 'error' | 'info' | 'pending' {
  switch (status) {
    case 'Processed':
      return 'info';
    case 'Processed with Errors':
      return 'warning';
    case 'Failed':
      return 'error';
    case 'Completed':
      return 'success';
    case 'Processing':
      return 'pending';
    default:
      return 'pending';
  }
}

function formatDate(dateString: string): string {
  return formatMediumDateTime(dateString);
}

const ImportJobsTab: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [jobTypeFilter, setJobTypeFilter] = useState(searchParams.get('jobType') || 'all');
  const isPendingReview = searchParams.get(COMPLETION_PARAM) === COMPLETION_PENDING;

  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'import-jobs-page-size',
    defaultPageSize: 25,
  });

  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam,
  } = useColumnFilters({ columns: importJobColumns });

  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: columnSortMapping,
    defaultSort: { field: 'started_at', direction: 'DESC' },
    onPageReset: () => setCurrentPage(1),
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }

    if (jobTypeFilter !== 'all') {
      params.set('jobType', jobTypeFilter);
    } else {
      params.delete('jobType');
    }

    setSearchParams(params, { replace: true });
  }, [searchQuery, jobTypeFilter, searchParams, setSearchParams]);

  useEffect(() => {
    resetToFirstPage();
  }, [searchQuery, jobTypeFilter, isPendingReview, debouncedColumnFilters, resetToFirstPage]);

  const {
    jobs,
    isLoading,
    error,
    isFetching,
    filterCounts,
    totalItems,
    totalPages,
    refreshData,
  } = useImportJobsPageData({
    queryKey: ['importJobs', searchQuery, jobTypeFilter, isPendingReview, currentPage, pageSize, sortConfig, debouncedColumnFilters],
    pageSize,
    queryFn: () => {
      const filters = {
        search: searchQuery || undefined,
        jobType: jobTypeFilter !== 'all' ? jobTypeFilter : undefined,
        completion: isPendingReview ? COMPLETION_PENDING : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sortBy: sortConfig.field,
        sortOrder: sortConfig.direction.toLowerCase(),
      };
      return importService.getImportHistory(filters);
    },
  });

  const [templatesInfoOpen, setTemplatesInfoOpen] = useState(false);

  const [triggerMenuOpen, setTriggerMenuOpen] = useState(false);
  const triggerMenuRef = useRef<HTMLDivElement>(null);

  useDismissable(triggerMenuRef, triggerMenuOpen, () => setTriggerMenuOpen(false));

  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  useDismissable(templateMenuRef, templateMenuOpen, () => setTemplateMenuOpen(false));

  const handleDownloadTemplate = async (importType: ImportType, label: string) => {
    setTemplateMenuOpen(false);
    try {
      await importService.downloadImportTemplate(importType);
    } catch (error) {
      console.error(`Failed to download the ${label} template`, error);
      showToast.error(`Failed to download the ${label} template`);
    }
  };

  const triggerMutation = useMutation({
    mutationFn: (jobName: string) => importService.triggerScheduledJob(jobName),
    onSuccess: (_data, jobName) => {
      showToast.success(`${jobName} triggered successfully. Check history for progress.`);
      setTriggerMenuOpen(false);
      refreshData();
    },
    onError: (_error, jobName) => {
      showToast.error(`Failed to trigger ${jobName}`);
      setTriggerMenuOpen(false);
    },
  });

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setJobTypeFilter(filter);
    setCurrentPage(1);
  };

  const handleResetPendingReview = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(COMPLETION_PARAM);
    setSearchParams(params, { replace: true });
  };

  const handleRowClick = (record: ImportHistoryRecord) => {
    navigate(`/admin/import-jobs/${record.id}`);
  };

  const renderCell = (column: TableColumn, record: ImportHistoryRecord) => {
    switch (column.key) {
      case 'started_at':
        return <span className={styles.dateCell}>{formatDate(record.started_at)}</span>;

      case 'job_type':
        return (
          <Badge variant={record.job_type === 'scheduled' ? 'purple' : 'default'}>
            {getJobTypeBadgeLabel(record)}
          </Badge>
        );

      case 'import_type':
        return <span className={styles.textCell}>{capitalize(record.import_type)}</span>;

      case 'file_name':
        return <span className={styles.fileName}>{record.file_name}</span>;

      case 'result': {
        const status = getDisplayStatus(record);
        return (
          <Badge variant={getResultBadgeVariant(status)}>
            {status}
          </Badge>
        );
      }

      default:
        return '-';
    }
  };

  if (error) {
    return (
      <div className={styles.errorState}>
        <p>Error loading import jobs. Please try again.</p>
      </div>
    );
  }

  return (
    <>
      <ListPageHeader
        icon={<Import size={24} />}
        title="Import Jobs"
        count={filterCounts.all || 0}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search by file name, import type..."
        actions={
          <>
            <button
              type="button"
              className={styles.infoToggle}
              onClick={() => setTemplatesInfoOpen(prev => !prev)}
              aria-expanded={templatesInfoOpen}
              aria-controls="import-templates-info"
              data-test-id="import-templates-info-toggle"
            >
              <Info size={16} aria-hidden="true" />
              How import templates work
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={clsx(styles.infoToggleChevron, templatesInfoOpen && styles.infoToggleChevronOpen)}
              />
            </button>
            <div className={styles.dropdown} ref={templateMenuRef}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setTemplateMenuOpen(prev => !prev)}
                dataTestId="download-template-dropdown-button"
              >
                <Download size={16} />
                Download Template
                <ChevronDown size={14} />
              </Button>
              {templateMenuOpen && (
                <div className={styles.dropdownMenu} data-test-id="download-template-menu">
                  {templateOptions.map(({ importType, label }) => (
                    <button
                      key={importType}
                      className={styles.dropdownMenuItem}
                      onClick={() => void handleDownloadTemplate(importType, label)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.dropdown} ref={triggerMenuRef}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setTriggerMenuOpen(prev => !prev)}
                disabled={triggerMutation.isPending}
              >
                <Play size={16} />
                {triggerMutation.isPending ? 'Running...' : 'Trigger Job'}
                <ChevronDown size={14} />
              </Button>
              {triggerMenuOpen && (
                <div className={styles.dropdownMenu}>
                  <button
                    className={styles.dropdownMenuItem}
                    onClick={() => triggerMutation.mutate('Import Contracts')}
                    disabled={triggerMutation.isPending}
                  >
                    Import Contracts
                  </button>
                  <button
                    className={styles.dropdownMenuItem}
                    onClick={() => triggerMutation.mutate('Import Contacts')}
                    disabled={triggerMutation.isPending}
                  >
                    Import Contacts
                  </button>
                </div>
              )}
            </div>
          </>
        }
        footer={
          templatesInfoOpen && (
            <InfoBanner
              id="import-templates-info"
              data-test-id="import-templates-info-banner"
              className={styles.templatesInfo}
            >
              Download a template, fill in one row per record and keep the column names exactly as they are.
              Save it as <strong>CSV (UTF-8)</strong> and upload it to the import storage in Azure:
              the <strong>contacts/</strong> folder for contacts, <strong>contracts/</strong> for contracts.
              The import job picks up new files every night (contacts first, then contracts) and the results
              appear in this list. To process an uploaded file right away, use <strong>Trigger Job</strong>.
              If some rows fail, open the job and use <strong>Upload Corrected File</strong> to fix just those
              rows. Contracts can also be uploaded immediately with the <strong>Import wizard</strong>.
            </InfoBanner>
          )
        }
      />

      {isPendingReview && <DashboardFilterChip onReset={handleResetPendingReview} />}

      <FilterButtons
        filters={jobTypeFilters}
        activeFilter={jobTypeFilter}
        onFilterChange={handleFilterChange}
        filterCounts={filterCounts}
        dataTestId="import-jobs-filter"
      />

      {isFetching && !isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner} />
        </div>
      )}

      <div className={styles.importJobsTable} data-test-id="import-jobs-page">
        <RZTable
          data={jobs}
          columns={importJobColumns}
          getRowKey={(record) => record.id}
          renderCell={renderCell}
          onRowClick={handleRowClick}
          sorting={{
            sortConfig,
            onSort: handleSort,
            isSorted,
          }}
          columnFilters={{
            values: columnFilterValues,
            onChange: setColumnFilter,
            onClearAll: clearColumnFilters,
            hasActiveFilters: hasActiveColumnFilters,
          }}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
            itemName: 'jobs',
            pageSizeOptions: [25, 50, 100],
          }}
          state={{
            isLoading,
            isEmpty: jobs.length === 0,
            loadingMessage: 'Loading import jobs...',
          }}
          emptyState={
            <RZTable.Empty
              icon={<Import size={48} />}
              title="No import jobs found"
              description="Import jobs will appear here after the first import is completed."
            />
          }
        />
      </div>
    </>
  );
};

export default ImportJobsTab;
